/**
 * t297u: per-person preferences that follow you to every device (collab.user_prefs, GET/PATCH /me/prefs). One jsonb
 * object of named keys. `seen_tips` (screen id → when it was dismissed) is the first; others (e.g. the schedule's
 * day-view hours, still in localStorage) can move here later.
 */
export interface UserPrefs {
  /** First-visit quick tips: the screens whose tips you've dismissed, with when. */
  seen_tips?: Record<string, string>;
  [key: string]: unknown;
}
export interface UserPrefsResponse {
  prefs: UserPrefs;
  /** True for the smoke-test and other system accounts: they never see first-visit tips. */
  tips_off: boolean;
}

export const PREF_KEY = /^[a-z][a-z0-9_]{0,39}$/;
export const PREFS_MAX_BYTES = 16_384;

/**
 * Applies a PATCH: each top-level key is replaced, or removed when null. A plain object merges one level deep into an
 * existing object (so marking one tip seen on a phone doesn't undo one seen on a computer), and a null inside it
 * removes that entry. Returns null when the patch is invalid (bad key, not an object, too big).
 */
export function mergePrefs(current: UserPrefs, patch: unknown): UserPrefs | null {
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) return null;
  const isObj = (v: unknown): v is Record<string, unknown> => Boolean(v) && typeof v === 'object' && !Array.isArray(v);
  const out: UserPrefs = { ...current };
  for (const [k, v] of Object.entries(patch as Record<string, unknown>)) {
    if (!PREF_KEY.test(k)) return null;
    if (v === null) { delete out[k]; continue; }
    if (isObj(v) && isObj(out[k])) {
      const inner: Record<string, unknown> = { ...(out[k] as Record<string, unknown>) };
      for (const [ik, iv] of Object.entries(v)) { if (iv === null) delete inner[ik]; else inner[ik] = iv; }
      out[k] = inner;
    } else if (isObj(v)) {
      out[k] = Object.fromEntries(Object.entries(v).filter(([, iv]) => iv !== null));
    } else out[k] = v;
  }
  if (JSON.stringify(out).length > PREFS_MAX_BYTES) return null;
  return out;
}

/** Did this patch change anything? (So "Saved" only shows on a real change.) */
export const prefsChanged = (a: UserPrefs, b: UserPrefs) => JSON.stringify(sortKeys(a)) !== JSON.stringify(sortKeys(b));
function sortKeys(v: unknown): unknown {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return v;
  return Object.fromEntries(Object.keys(v as object).sort().map(k => [k, sortKeys((v as Record<string, unknown>)[k])]));
}
